/**
 * Confirmation dialog shown when the user dismisses a breadcrumb chip whose
 * subtree contains unsaved edits. Offers Discard / Cancel — Save is omitted
 * here because the dismissed node may not be the currently-active edit owner
 * (saving M tables' changes in one click is a separate, larger workflow).
 * The user can save through the regular toolbar first, then dismiss.
 */

import { AlertTriangle, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import { Button } from '../../../../components/ui/button';

type DismissBreadcrumbConfirmDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly pendingChangeCount: number;
  readonly onDiscard: () => void;
};

export function DismissBreadcrumbConfirmDialog({
  open,
  onOpenChange,
  pendingChangeCount,
  onDiscard,
}: DismissBreadcrumbConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="dismiss-breadcrumb-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Discard unsaved edits?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Dismissing this breadcrumb chip will remove{' '}
            {pendingChangeCount} unsaved change{pendingChangeCount !== 1 ? 's' : ''}{' '}
            in its subtree. To keep them, cancel and save from the toolbar first.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="dismiss-breadcrumb-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onDiscard}
            data-testid="dismiss-breadcrumb-discard"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Discard
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
